import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import { money, invoiceTotal } from '../../shared/formatters';
import Breadcrumbs from '../../shared/components/Breadcrumbs';
import StatusPill from '../../shared/components/StatusPill';
import Skeleton from '../../shared/components/Skeleton';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

let lineSeq = 0;
const nextLineId = () => `new_line_${Date.now()}_${lineSeq++}`;

const emptyLine = () => ({ id: nextLineId(), description: '', quantity: 1, unit_price: 0 });

const DOC_TYPE_LABELS = {
  invoice: 'Invoice',
  estimate: 'Estimate',
  quote: 'Quotation',
};

// Build the initial form state from whichever source applies. Used both at
// mount and when the source record arrives late (session still loading on
// a direct URL load — data.invoices is empty at first render).
const initialForm = ({ existing, duplicateSource, searchParams }) => {
  if (duplicateSource) {
    // Duplicate (1.6): copy content, force a fresh document — new number is
    // hook-assigned, due date empty, status draft, no paid_date/pdf carried.
    return {
      client_id: duplicateSource.client_id,
      docType: duplicateSource.docType || 'invoice',
      status: 'draft',
      due_date: '',
      linked_job_id: duplicateSource.linked_job_id || duplicateSource.job_id || '',
      line_items: duplicateSource.line_items.map((item) => ({
        id: nextLineId(),
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
      })),
    };
  }

  if (existing) {
    return {
      client_id: existing.client_id,
      docType: existing.docType || 'invoice',
      status: existing.status || 'draft',
      due_date: String(existing.due_date || '').slice(0, 10),
      linked_job_id: existing.linked_job_id || existing.job_id || '',
      line_items: existing.line_items.map((item) => ({ ...item })),
    };
  }

  return {
    client_id: searchParams.get('client') || '',
    docType: 'invoice',
    status: 'draft',
    due_date: '',
    linked_job_id: '',
    line_items: [emptyLine()],
  };
};

// WYSIWYG inline invoice editor — renders as a branded document with
// click-to-edit fields (spec 1.4). Supports copy link (1.3), line item
// templates (1.5), and duplicate (1.6).
export default function InvoiceForm() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, add, update, remove, ready } = useData();
  const { notify } = useToast();

  const editing = Boolean(invoiceId);
  const existing = editing ? data.invoices.find((item) => item.id === invoiceId) : null;

  // Duplicate support (1.6): load source invoice when ?duplicate= is set
  const duplicateSourceId = searchParams.get('duplicate');
  const duplicateSource = useMemo(
    () => (duplicateSourceId && !editing ? data.invoices.find((i) => i.id === duplicateSourceId) : null),
    [duplicateSourceId, editing, data.invoices]
  );

  // A blank new-invoice form can initialize immediately; edit/duplicate
  // forms must wait for their source record (guards the direct-URL race
  // where data.invoices is still empty at mount).
  const [form, setForm] = useState(() => initialForm({ existing, duplicateSource, searchParams }));
  const [initialized, setInitialized] = useState(!editing && !duplicateSourceId);

  useEffect(() => {
    if (initialized) return;
    if (editing && existing) {
      setForm(initialForm({ existing }));
      setInitialized(true);
    } else if (!editing && duplicateSource) {
      setForm(initialForm({ duplicateSource }));
      setInitialized(true);
    }
  }, [initialized, editing, existing, duplicateSource]);

  const [busy, setBusy] = useState(false);
  const [showTemplateSelect, setShowTemplateSelect] = useState(false);

  const client = data.clients.find((item) => item.id === form.client_id);
  const clientJobs = data.jobs.filter((item) => item.client_id === form.client_id);
  const tenant = data.tenant;
  const total = useMemo(() => invoiceTotal(form), [form]);

  // Hooks are all above — only now may we bail out early (edit/duplicate
  // sources still loading on a direct URL hit).
  if (!initialized || !ready) {
    return <div className="standalone-page"><Skeleton rows={6} /></div>;
  }

  const backTo = () => (form.client_id ? `/admin/clients/${form.client_id}` : '/admin/clients');

  const setLine = (id, patch) => setForm((current) => ({
    ...current,
    line_items: current.line_items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  }));

  const addLine = () => setForm((current) => ({ ...current, line_items: [...current.line_items, emptyLine()] }));

  const removeLine = (id) => setForm((current) => ({
    ...current,
    line_items: current.line_items.length > 1 ? current.line_items.filter((item) => item.id !== id) : current.line_items,
  }));

  const save = async (statusOverride) => {
    if (!form.client_id) {
      notify('Pick a client first', 'error');
      return;
    }
    if (!form.line_items.some((item) => item.description.trim())) {
      notify('Add at least one line item', 'error');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        linked_job_id: form.linked_job_id || null,
        status: statusOverride || form.status,
      };
      if (editing) {
        await update('invoices', invoiceId, { ...existing, ...payload });
      } else {
        await add('invoices', payload);
      }
      notify(editing ? 'Invoice updated' : 'Invoice created');
      navigate(backTo());
    } catch (err) {
      notify(err.message || 'Failed to save invoice', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Copy link (1.3)
  const copyLink = async () => {
    if (!existing?.shareToken) return;

    const url = existing.shareUrl || `${window.location.origin}/i/${existing.shareToken}`;

    // Try modern clipboard API first
    try {
      await navigator.clipboard.writeText(url);
      notify('Invoice link copied');
      return;
    } catch (e) {
      // Fallback: textarea method
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        notify('Invoice link copied');
      } catch (err) {
        notify('Failed to copy link', 'error');
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  // Insert template (1.5)
  const insertTemplate = (template) => {
    const hasContent = form.line_items.some((item) => item.description.trim());

    if (hasContent && !window.confirm('Replace current line items with this template?')) {
      return;
    }

    setForm((current) => ({
      ...current,
      line_items: template.items.map((item) => ({
        id: nextLineId(),
        description: item.description,
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
      })),
    }));

    notify(`Inserted template: ${template.name}`);
    setShowTemplateSelect(false);
  };

  // Save lines as template (1.5)
  const saveAsTemplate = () => {
    if (!form.line_items.some((item) => item.description.trim())) {
      notify('Add at least one line item before saving as template', 'error');
      return;
    }

    const name = window.prompt('Template name:');
    if (!name || !name.trim()) return;

    add('lineItemTemplates', {
      name: name.trim(),
      items: form.line_items.map((item) => ({
        description: item.description,
        qty: item.quantity,
        unit_price: item.unit_price,
      })),
      sort: 0,
    }).then(() => {
      notify('Template saved');
    }).catch((err) => {
      notify(err.message || 'Failed to save template', 'error');
    });
  };

  // Delete template (1.5)
  const deleteTemplate = (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;

    remove('lineItemTemplates', template.id)
      .then(() => notify('Template deleted'))
      .catch((err) => notify(err.message || 'Failed to delete template', 'error'));
  };

  const docLabel = DOC_TYPE_LABELS[form.docType] || 'Invoice';
  const titleText = duplicateSource
    ? `New invoice (duplicated from ${duplicateSource.number || 'source'})`
    : editing
    ? `Edit ${existing?.number || 'invoice'}`
    : `New ${docLabel.toLowerCase()}`;

  return (
    <div className="invoice-editor">
      <Breadcrumbs items={[{ label: 'Clients', to: '/admin/clients' }, { label: editing ? (existing?.number || 'Edit invoice') : 'New invoice' }]} />

      <header className="invoice-editor-header">
        <div>
          <span className="eyebrow">Billing</span>
          <h1>{titleText}</h1>
        </div>
        <div className="header-actions">
          <button className="secondary-button" onClick={() => navigate(backTo())} disabled={busy}>Cancel</button>
          <button className="secondary-button" onClick={() => save()} disabled={busy}>{busy ? 'Saving…' : 'Save draft'}</button>
          <button className="primary-button" onClick={() => save('sent')} disabled={busy}>{busy ? 'Saving…' : 'Save & mark sent'}</button>
          {editing && existing?.shareToken && (
            <button className="secondary-button" onClick={copyLink} title="Copy invoice link">
              <SafeIcon icon={Icons.Link} />
              Copy link
            </button>
          )}
          {!editing && (
            <button className="secondary-button" disabled title="Save the invoice first">
              <SafeIcon icon={Icons.Link} />
              Copy link
            </button>
          )}
        </div>
      </header>

      {/* Branded document view */}
      <div className="portal-card invoice-editor-document">
        {/* Branded header band */}
        <div className="brand-band">
          <div className="brand-band-content">
            <strong>{tenant?.name || 'Tenant'}</strong>
            <StatusPill status={form.status} />
          </div>
        </div>

        {/* Document header */}
        <div className="document-header">
          <div>
            <span className="eyebrow">{docLabel}</span>
            <h1>{existing?.number || '—'}</h1>
          </div>

          {/* Inline editable fields */}
          <div className="invoice-meta-inline">
            {/* Client (editable when not editing existing) */}
            <div className="meta-field">
              <span className="meta-label">Client</span>
              {editing ? (
                <span className="meta-value">{client?.name || '—'}</span>
              ) : (
                <select
                  value={form.client_id}
                  onChange={(e) => setForm((c) => ({ ...c, client_id: e.target.value, linked_job_id: '' }))}
                  className="inline-select"
                >
                  <option value="">Choose a client…</option>
                  {data.clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              )}
            </div>

            {/* Document type */}
            <div className="meta-field">
              <span className="meta-label">Type</span>
              <select
                value={form.docType}
                onChange={(e) => setForm((c) => ({ ...c, docType: e.target.value }))}
                className="inline-select"
              >
                {Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div className="meta-field">
              <span className="meta-label">Status</span>
              <select
                value={form.status}
                onChange={(e) => setForm((c) => ({ ...c, status: e.target.value }))}
                className="inline-select"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            {/* Due date */}
            <div className="meta-field">
              <span className="meta-label">Due date</span>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm((c) => ({ ...c, due_date: e.target.value }))}
                className="inline-input"
              />
            </div>

            {/* Linked job */}
            <div className="meta-field">
              <span className="meta-label">Linked job</span>
              <select
                value={form.linked_job_id}
                onChange={(e) => setForm((c) => ({ ...c, linked_job_id: e.target.value }))}
                className="inline-select"
              >
                <option value="">None</option>
                {clientJobs.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Line items */}
        <table className="line-items-table">
          <thead>
            <tr>
              <th className="desc-col">Description</th>
              <th className="qty-col">Qty</th>
              <th className="price-col">Price</th>
              <th className="total-col">Total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {form.line_items.map((item) => (
              <tr key={item.id}>
                <td className="desc-col">
                  <input
                    value={item.description}
                    onChange={(e) => setLine(item.id, { description: e.target.value })}
                    placeholder="Description"
                    className="inline-input"
                  />
                </td>
                <td className="qty-col">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={item.quantity}
                    onChange={(e) => setLine(item.id, { quantity: Number(e.target.value) })}
                    className="inline-input numeric"
                  />
                </td>
                <td className="price-col">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => setLine(item.id, { unit_price: Number(e.target.value) })}
                    className="inline-input numeric"
                  />
                </td>
                <td className="total-col">{money((Number(item.quantity) || 0) * (Number(item.unit_price) || 0))}</td>
                <td>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Remove line"
                    onClick={() => removeLine(item.id)}
                    disabled={form.line_items.length <= 1}
                  >
                    <SafeIcon icon={Icons.X} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="invoice-editor-actions">
          <button type="button" className="secondary-button small-button" onClick={addLine}>+ Add line</button>
        </div>

        <div className="invoice-total"><span>Total</span><strong>{money(total)}</strong></div>
      </div>

      {/* Template controls (1.5) */}
      <section className="panel">
        <div className="panel-head">
          <h3>Line item templates</h3>
          <button type="button" className="secondary-button small-button" onClick={saveAsTemplate}>
            Save lines as template
          </button>
        </div>

        {data.lineItemTemplates.length > 0 ? (
          <div className="template-list">
            {data.lineItemTemplates.map((template) => (
              <div key={template.id} className="template-item">
                <div className="template-info">
                  <strong>{template.name}</strong>
                  <small>{template.items?.length || 0} items</small>
                </div>
                <div className="template-actions">
                  <button
                    type="button"
                    className="secondary-button small-button"
                    onClick={() => insertTemplate(template)}
                  >
                    Insert
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Delete template"
                    onClick={() => deleteTemplate(template)}
                  >
                    <SafeIcon icon={Icons.X} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No templates yet — save your current lines as a template to reuse them.</p>
          </div>
        )}
      </section>
    </div>
  );
}